import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Loader2, Sparkles, Award, Package, AlertCircle,
  CheckCircle2, ArrowRight, RefreshCw, Star, X, Zap, ChevronDown,
  Leaf, Shield, FlaskConical, Heart, Fish, Wheat, Drumstick,
  Baby, Dog, PawPrint, Bone, Activity, Salad, Droplet,
} from 'lucide-react';

// Pick up to 3 benefit icons for a product match
const deriveBenefitIcons = (product = {}) => {
  const icons = [];
  const seen = new Set();
  const add = (key, Icon, label) => {
    if (seen.has(key)) return;
    seen.add(key);
    icons.push({ key, Icon, label });
  };

  const type = (product.type || '').toLowerCase();
  if (type === 'grain_free') add('grain_free', Leaf, 'Grain Free');
  else if (type === 'hydrolysed') add('hydrolysed', FlaskConical, 'Hydrolysed');
  else if (type === 'superfoods') add('superfoods', Salad, 'Superfoods');

  const lifestage = (product.lifestage || '').toLowerCase();
  if (lifestage === 'puppy') add('puppy', Baby, 'Puppy Recipe');
  else if (lifestage === 'senior') add('senior', Heart, 'Senior Recipe');
  else if (lifestage === 'adult') add('adult', Dog, 'Adult Recipe');

  const breed = (product.breed_size || '').toLowerCase();
  if (breed === 'small') add('small', PawPrint, 'Small Breed');
  else if (breed === 'large') add('large', Dog, 'Large Breed');

  const tags = (product.tags || []).map(t => (t || '').toLowerCase());
  const benefits = (product.benefits || '').toLowerCase();
  const ingredients = (product.ingredients || '').toLowerCase();
  const blob = `${tags.join(' ')} ${benefits} ${ingredients}`;

  if (/fish|salmon|trout|tuna/.test(blob)) add('fish', Fish, 'Fresh Fish');
  else if (/chicken|lamb|turkey|duck|beef/.test(blob)) add('meat', Drumstick, 'Fresh Meat');
  if (/joint|hip|mobility|glucosamine|chondroitin/.test(blob)) add('joint', Bone, 'Joint Support');
  if (/digestive|gut|sensitive|prebiotic|probiotic|fos|mos/.test(blob)) add('digestive', Shield, 'Digestive Care');
  if (/coat|skin|omega/.test(blob)) add('coat', Droplet, 'Skin & Coat');
  if (/energy|active|protein/.test(blob)) add('active', Activity, 'Active Dogs');
  if (/natural|no artificial|preservation/.test(blob)) add('natural', Leaf, 'All Natural');
  if (/grain|cereal|wheat|corn/.test(blob) && type !== 'grain_free') add('grain', Wheat, 'With Grains');

  return icons.slice(0, 3);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ---------- Quality score ----------
// Simple, defensible score for the scanned bag. Used purely to give live
// viewers a quick at-a-glance "how good is this food" number. Score range 0-100.
const EMPTY_VALUES = [
  '', 'none', 'n/a', 'na', 'unknown', 'not visible', 'not specified',
  'not available', 'not provided', 'not listed', 'not shown', 'unspecified',
];
const filled = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') {
    const low = v.trim().toLowerCase();
    return !!low && !EMPTY_VALUES.includes(low);
  }
  return true;
};

const calcBagScore = (a) => {
  if (!a) return { score: 0, label: 'Unknown', breakdown: [] };
  let score = 40; // baseline — any recognised dog food
  const breakdown = [];

  // Grain-free or hydrolysed recipe bonus
  const type = (a.type || '').toLowerCase();
  if (type === 'grain_free') { score += 15; breakdown.push({ label: 'Grain-free recipe', pts: 15 }); }
  else if (type === 'hydrolysed') { score += 18; breakdown.push({ label: 'Hydrolysed recipe', pts: 18 }); }
  else if (type === 'superfoods') { score += 10; breakdown.push({ label: 'Superfoods recipe', pts: 10 }); }

  // Named single protein
  const prot = (a.protein_source || '').trim();
  if (filled(prot) && !/derivative|meal|meat /i.test(prot) && prot.split(',').length <= 2) {
    score += 15; breakdown.push({ label: `Named protein: ${prot}`, pts: 15 });
  } else if (filled(prot)) {
    score += 6; breakdown.push({ label: 'Protein listed', pts: 6 });
  }

  // Ingredients readable
  if (filled(a.ingredients)) {
    const ing = a.ingredients.toLowerCase();
    const bad = /(derivative|meal|by-?product|preservative|colourant|sugar\b|corn syrup)/.test(ing);
    if (bad) { score += 3; breakdown.push({ label: 'Ingredients include fillers', pts: 3 }); }
    else { score += 12; breakdown.push({ label: 'Clean ingredients', pts: 12 }); }
  }

  // Analytical constituents parsed for protein %
  if (filled(a.analytical_constituents)) {
    const m = (a.analytical_constituents || '').match(/protein[^\d]*(\d{1,2}(?:\.\d)?)/i);
    if (m) {
      const p = parseFloat(m[1]);
      if (p >= 28) { score += 15; breakdown.push({ label: `Protein ${p}% (high)`, pts: 15 }); }
      else if (p >= 22) { score += 10; breakdown.push({ label: `Protein ${p}% (good)`, pts: 10 }); }
      else if (p >= 18) { score += 5; breakdown.push({ label: `Protein ${p}% (moderate)`, pts: 5 }); }
      else { breakdown.push({ label: `Protein ${p}% (low)`, pts: 0 }); }
    } else {
      score += 4; breakdown.push({ label: 'Nutrition panel listed', pts: 4 });
    }
  }

  // Special needs claim
  if (filled(a.special_needs)) {
    score += 5; breakdown.push({ label: `Claim: ${a.special_needs}`, pts: 5 });
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  const label =
    score >= 80 ? 'Excellent' :
    score >= 65 ? 'Good' :
    score >= 50 ? 'Average' :
    score >= 35 ? 'Below Average' : 'Poor';
  return { score, label, breakdown };
};

const scoreColor = (s) => s >= 80 ? '#16a34a' : s >= 65 ? '#65a30d' : s >= 50 ? '#ca8a04' : s >= 35 ? '#ea580c' : '#dc2626';

// ---------- Main component ----------
export const LiveMatchScreen = () => {
  const [ref, setRef] = useState('');
  const [submission, setSubmission] = useState(null);
  const [loadingRef, setLoadingRef] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [hasRescanned, setHasRescanned] = useState(false);
  const [matching, setMatching] = useState(false);
  const [matches, setMatches] = useState(null);

  const [expandedMatch, setExpandedMatch] = useState(0); // top pick auto-open

  const reset = () => {
    setRef(''); setSubmission(null); setMatches(null); setHasRescanned(false); setExpandedMatch(0);
  };

  const fetchReference = async (e) => {
    e?.preventDefault?.();
    const code = ref.trim().toUpperCase();
    if (!code) return;
    setLoadingRef(true);
    setSubmission(null);
    setMatches(null);
    setExpandedMatch(0);
    setHasRescanned(false);
    try {
      const res = await axios.get(`${API}/admin/tiktok-live/by-reference/${code}`, { headers: authHeaders() });
      setSubmission(res.data);
      toast.success(`Loaded ${res.data.reference} — ${res.data.dog_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reference not found');
    } finally {
      setLoadingRef(false);
    }
  };

  const handleRescan = async () => {
    if (!submission?.front_image_url || !submission?.back_image_url) {
      toast.error('Original images not available on this submission');
      return;
    }
    setRescanning(true);
    try {
      // Fetch the image files from backend, convert to base64, re-send to analyze
      const [frontBlob, backBlob] = await Promise.all([
        fetch(`${BACKEND}${submission.front_image_url}`).then(r => r.blob()),
        fetch(`${BACKEND}${submission.back_image_url}`).then(r => r.blob()),
      ]);
      const toB64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const [front_image, back_image] = await Promise.all([toB64(frontBlob), toB64(backBlob)]);
      const res = await axios.post(`${API}/ted-ai/analyze`, { front_image, back_image });
      if (res.data.status === 'success') {
        setSubmission(prev => ({ ...prev, analysis: res.data.analysis }));
        setHasRescanned(true);
        toast.success('Ted re-scanned the bag');
      } else {
        toast.error(res.data.message || 'Re-scan failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-scan failed');
    } finally {
      setRescanning(false);
    }
  };

  const handleFindMatches = async () => {
    if (!submission?.analysis) return;
    setMatching(true);
    setMatches(null);
    setExpandedMatch(0);
    try {
      const a = submission.analysis;
      const res = await axios.post(`${API}/ted-ai/match`, {
        brand: String(a.brand || ''),
        product_name: String(a.product_name || ''),
        protein_source: a.protein_source || null,
        ingredients: a.ingredients || null,
        type: a.type || null,
        lifestage: a.lifestage || null,
        breed_size: a.breed_size || null,
        special_needs: a.special_needs || null,
        bag_weight: a.bag_weight || null,
        front_image_url: submission.front_image_url,
        back_image_url: submission.back_image_url,
        email: submission.email,
        dog_name: submission.dog_name,
      });
      if (res.data.status === 'success' || res.data.matches) {
        setMatches((res.data.matches || []).slice(0, 3));
      } else {
        toast.error(res.data.message || 'No matches returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Match failed');
    } finally {
      setMatching(false);
    }
  };

  const bagScore = hasRescanned && submission?.analysis ? calcBagScore(submission.analysis) : null;

  return (
    <div className="min-h-[calc(100vh-200px)]">
      {/* Top bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-extrabold text-[#1f6d62] flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-[#a0da39]" />
            Live Show Presenter
          </h3>
          <p className="text-xs text-[#666]">Use this screen on-camera during the TikTok Live broadcast.</p>
        </div>
        {submission && (
          <button
            onClick={reset}
            className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-[#222] bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg"
            data-testid="live-reset"
          >
            <X className="w-3.5 h-3.5" /> Clear & New Reference
          </button>
        )}
      </div>

      {/* 3-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* =========== LEFT: Reference + bag =========== */}
        <div className="bg-white rounded-2xl border-2 border-[#1f6d62]/20 p-5 space-y-4" data-testid="live-col-left">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1f6d62] text-white flex items-center justify-center font-black text-sm">1</div>
            <h4 className="font-extrabold text-[#222]">Their Bag</h4>
          </div>

          <form onSubmit={fetchReference} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="TT-5188"
              className="w-full pl-11 pr-3 py-3 text-xl font-extrabold tracking-widest text-[#1f6d62] uppercase rounded-xl border-2 border-dashed border-[#a0da39] bg-[#f4f7ea] focus:outline-none focus:ring-2 focus:ring-[#1f6d62]"
              data-testid="live-ref-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loadingRef || !ref.trim()}
              className="mt-2 w-full bg-[#1f6d62] hover:bg-[#174f46] text-white font-bold uppercase tracking-wide py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="live-lookup-btn"
            >
              {loadingRef ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Load Reference
            </button>
          </form>

          <AnimatePresence mode="wait">
            {submission ? (
              <motion.div
                key="loaded"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 rounded-full bg-[#a0da39] text-[#1f6d62] font-black tracking-widest">
                    {submission.reference}
                  </span>
                  <span className="text-[#666]">· <strong className="text-[#222]">{submission.dog_name}</strong></span>
                </div>

                {submission.front_image_url && (
                  <img
                    src={`${BACKEND}${submission.front_image_url}`}
                    alt="Front of bag"
                    className="w-full h-56 object-cover rounded-xl border border-gray-200"
                  />
                )}

                <div className="space-y-1">
                  <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold">Brand</p>
                  <p className="text-2xl font-extrabold text-[#222] leading-tight">
                    {filled(submission.analysis?.brand) ? submission.analysis.brand : 'Unknown'}
                  </p>
                  <p className="text-base font-semibold text-[#444] leading-snug">
                    {filled(submission.analysis?.product_name) ? submission.analysis.product_name : '—'}
                  </p>
                </div>

                {filled(submission.analysis?.ingredients) && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">Ingredients</p>
                    <p className="text-xs text-[#333] leading-relaxed line-clamp-6">{submission.analysis.ingredients}</p>
                  </div>
                )}

                {filled(submission.analysis?.analytical_constituents) && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">Nutrition</p>
                    <p className="text-xs text-[#333]">{submission.analysis.analytical_constituents}</p>
                  </div>
                )}

                <button
                  onClick={handleRescan}
                  disabled={rescanning}
                  className={`w-full bg-white hover:bg-gray-50 border-2 text-[#0FA49C] font-bold uppercase tracking-wide py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-all ${
                    hasRescanned ? 'border-gray-300 opacity-80' : 'border-[#0FA49C] shadow-md animate-pulse'
                  }`}
                  data-testid="live-rescan-btn"
                >
                  {rescanning ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Ted is reading…</>
                  ) : hasRescanned ? (
                    <><CheckCircle2 className="w-4 h-4" /> Scanned · Re-scan</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Scan with Ted AI</>
                  )}
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl"
              >
                <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-[#666]">Enter the reference your guest gave you to begin.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* =========== MIDDLE: Brand score =========== */}
        <div className="bg-white rounded-2xl border-2 border-[#1f6d62]/20 p-5 space-y-4" data-testid="live-col-middle">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1f6d62] text-white flex items-center justify-center font-black text-sm">2</div>
            <h4 className="font-extrabold text-[#222]">Brand Score</h4>
          </div>

          {bagScore ? (
            <>
              {/* Big score ring */}
              <div className="flex flex-col items-center py-3">
                <div className="relative w-48 h-48 sm:w-56 sm:h-56">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" strokeWidth="8" className="stroke-gray-100 fill-none" />
                    <circle
                      cx="50" cy="50" r="45" strokeWidth="8" strokeLinecap="round"
                      className="fill-none"
                      stroke={scoreColor(bagScore.score)}
                      strokeDasharray={2 * Math.PI * 45}
                      strokeDashoffset={2 * Math.PI * 45 * (1 - bagScore.score / 100)}
                      style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-6xl font-black leading-none" style={{ color: scoreColor(bagScore.score) }} data-testid="live-score-number">
                      {bagScore.score}
                    </span>
                    <span className="text-xs text-[#666] font-semibold uppercase tracking-widest mt-1">/ 100</span>
                  </div>
                </div>
                <p
                  className="mt-4 text-xl font-extrabold uppercase tracking-wide"
                  style={{ color: scoreColor(bagScore.score) }}
                  data-testid="live-score-label"
                >
                  {bagScore.label}
                </p>
              </div>

              {/* Breakdown */}
              {bagScore.breakdown.length > 0 && (
                <div className="space-y-1.5">
                  {bagScore.breakdown.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5">
                      <span className="text-[#444] truncate mr-2">{b.label}</span>
                      <span className={`font-bold ${b.pts > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {b.pts > 0 ? `+${b.pts}` : b.pts}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleFindMatches}
                disabled={matching}
                className="w-full bg-[#a0da39] hover:bg-[#8ac82f] text-[#1f3d2f] font-extrabold uppercase tracking-wide py-4 rounded-xl text-base disabled:opacity-50 flex items-center justify-center gap-2 shadow-md"
                data-testid="live-match-btn"
              >
                {matching ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Finding matches…</>
                ) : (
                  <><Zap className="w-5 h-5" /> Find My Matches <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </>
          ) : (
            <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
              <Award className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-[#666]">
                {submission ? 'Tap "Scan with Ted AI" to unlock the brand score.' : "Load a reference first."}
              </p>
            </div>
          )}
        </div>

        {/* =========== RIGHT: Matches =========== */}
        <div className="bg-white rounded-2xl border-2 border-[#1f6d62]/20 p-5 space-y-4" data-testid="live-col-right">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1f6d62] text-white flex items-center justify-center font-black text-sm">3</div>
            <h4 className="font-extrabold text-[#222]">Top 3 UPF Matches</h4>
          </div>

          {matches && matches.length > 0 ? (
            <div className="space-y-3">
              {matches.map((m, i) => {
                const p = m.product || {};
                const benefitIcons = deriveBenefitIcons(p);
                const isOpen = expandedMatch === i;
                return (
                  <motion.div
                    key={p.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.12 }}
                    className={`relative rounded-xl border-2 overflow-hidden ${
                      i === 0 ? 'border-[#a0da39] bg-[#f4f7ea]' : 'border-gray-200 bg-white'
                    }`}
                    data-testid={`live-match-${i + 1}`}
                  >
                    {i === 0 && (
                      <span className="absolute -top-2 left-3 bg-[#a0da39] text-[#1f6d62] text-[10px] font-black uppercase px-2 py-0.5 rounded-full tracking-widest z-10">
                        Top Pick
                      </span>
                    )}

                    {/* Compact header — always visible, toggles expand on click */}
                    <button
                      type="button"
                      onClick={() => setExpandedMatch(isOpen ? -1 : i)}
                      className="w-full p-3 text-left hover:bg-black/[0.015] transition-colors"
                      data-testid={`live-match-${i + 1}-toggle`}
                    >
                      <div className="flex gap-3 items-center">
                        {p.image_url && (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            className="w-14 h-14 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-extrabold text-[#222] text-sm leading-tight line-clamp-2">{p.name}</p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <div className="flex items-center gap-0.5 bg-[#1f6d62] text-white px-2 py-0.5 rounded-full text-xs font-bold">
                                <Star className="w-3 h-3 fill-white" />
                                {m.match_score ?? '—'}%
                              </div>
                              <ChevronDown
                                className={`w-4 h-4 text-[#666] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              />
                            </div>
                          </div>
                          {p.type && (
                            <p className="text-[10px] text-[#0FA49C] font-bold uppercase tracking-wider mt-0.5">
                              {p.type.replace(/_/g, ' ')}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="px-3 pb-3 space-y-2.5 border-t border-black/10 pt-3">
                            {/* Benefit icons row */}
                            {benefitIcons.length > 0 && (
                              <div className="flex gap-2" data-testid={`live-match-${i + 1}-icons`}>
                                {benefitIcons.map((b) => (
                                  <div
                                    key={b.key}
                                    className="flex-1 flex flex-col items-center gap-1 bg-white rounded-lg border border-gray-200 py-2 px-1"
                                    title={b.label}
                                  >
                                    <div className="w-8 h-8 rounded-full bg-[#a0da39]/20 border border-[#a0da39]/40 flex items-center justify-center">
                                      <b.Icon className="w-4 h-4 text-[#1f6d62]" />
                                    </div>
                                    <span className="text-[9px] font-bold text-[#444] text-center uppercase tracking-wide leading-tight">
                                      {b.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {m.why_selected && (
                              <div className="rounded-lg bg-white border border-gray-200 p-2.5">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-[#0FA49C] mb-1">Why This Product</p>
                                <p className="text-xs text-[#333] leading-relaxed whitespace-pre-line">{m.why_selected}</p>
                              </div>
                            )}

                            {m.how_its_better && (
                              <div className="rounded-lg bg-[#1f6d62]/5 border border-[#1f6d62]/20 p-2.5">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-[#1f6d62] mb-1">How It's Better</p>
                                <p className="text-xs text-[#333] leading-relaxed whitespace-pre-line">{m.how_its_better}</p>
                              </div>
                            )}

                            {p.benefits && (
                              <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-[#666] mb-1">Product Highlights</p>
                                <p className="text-xs text-[#444] leading-relaxed">{p.benefits}</p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          ) : matching ? (
            <div className="text-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-[#0FA49C] mx-auto mb-2" />
              <p className="text-sm text-[#666]">Ted is finding the best matches…</p>
            </div>
          ) : (
            <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-[#666]">Matches will appear here.</p>
              <p className="text-xs text-[#999] mt-1">Tap "Find My Matches" once the score is shown.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveMatchScreen;
